/**
 * columnOps.ts - Column insert/delete/rename and column constraint methods.
 *
 * The insert and delete methods wrap the underlying `tableOps` column ops with undo checkpointing
 * and have no pure `Table -> Table` transform of their own. Renaming and setting a column's
 * constraint do transform the table, and persist the result themselves.
 */

import produce from 'immer';

import type {Cell} from 'notebook/tables/constants/CellConstants';
import type {ColumnConstraint, InsertableColumnInfo} from 'notebook/tables/constants/TableColumnConstants';
import {TableIndex} from 'notebook/tables/constants/TableSelectionConstants';
import {persistTable, type TableMutationParams} from 'notebook/tables/store/logic/mutations/mutationParams';
import {saveSelection} from 'notebook/tables/store/logic/mutations/selection';
import TableUtil from 'notebook/tables/TableUtil';

/**
 * Checkpoint a column range as the selection for undo before a structural column edit. The range
 * spans every row, matching the geometry the column header selects.
 */
function saveColumnRangeSelection(params: TableMutationParams, start: number, end: number): void {
  const {table} = params.getTableProps();
  const startIndex: TableIndex = [0, Math.min(start, end)];
  const endIndex: TableIndex = [table.table.length - 1, Math.max(start, end)];
  saveSelection(params, {multiStartIndex: startIndex, multiEndIndex: endIndex, multiSelIndex: startIndex});
}

/**
 * Delete a contiguous range of columns.
 *
 * Checkpoints the deleted columns as the selection for undo, then deletes them. `end` defaults to
 * `start`; no-op when `start` is null or when the table does not allow columns to be added and
 * removed.
 */
export function deleteColumns(params: TableMutationParams, start: number | null, end: number | null): void {
  const {table, canAddRemoveColumns} = params.getTableProps();
  if (!(canAddRemoveColumns ?? true)) {
    return;
  }
  const lastColumn = end ?? start;
  if (start == null || lastColumn == null) {
    return;
  }
  saveColumnRangeSelection(params, start, lastColumn);
  params.tableOps.deleteTableColumns(table.name, start, lastColumn);
}

/**
 * Insert as many columns to the left of the given range as the range spans.
 *
 * Checkpoints the range as the selection for undo before inserting. `end` defaults to `start`;
 * no-op when `start` is null or when the table does not allow columns to be added and removed.
 */
export function insertColumnsLeft(
  params: TableMutationParams,
  start: number | null,
  end: number | null,
  columnInfo: InsertableColumnInfo | {} = {}
): void {
  const {table, canAddRemoveColumns, getInitialCellValues} = params.getTableProps();
  if (!(canAddRemoveColumns ?? true)) {
    return;
  }
  const lastColumn = end ?? start;
  if (start == null || lastColumn == null) {
    return;
  }
  saveColumnRangeSelection(params, start, lastColumn);
  params.tableOps.insertTableColumnsLeft(
    table.name,
    start,
    lastColumn,
    columnInfo,
    getInitialCellValues?.(columnInfo)
  );
}

/**
 * Insert as many columns to the right of the given range as the range spans.
 *
 * Checkpoints the range as the selection for undo before inserting. `end` defaults to `start`;
 * no-op when `start` is null or when the table does not allow columns to be added and removed.
 */
export function insertColumnsRight(
  params: TableMutationParams,
  start: number | null,
  end: number | null,
  columnInfo: InsertableColumnInfo | {} = {}
): void {
  const {table, canAddRemoveColumns, getInitialCellValues} = params.getTableProps();
  if (!(canAddRemoveColumns ?? true)) {
    return;
  }
  const lastColumn = end ?? start;
  if (start == null || lastColumn == null) {
    return;
  }
  saveColumnRangeSelection(params, start, lastColumn);
  params.tableOps.insertTableColumnsRight(
    table.name,
    start,
    lastColumn,
    columnInfo,
    getInitialCellValues?.(columnInfo)
  );
}

/**
 * Rename the column at `index`, checkpointing the current selection for undo first.
 *
 * A structured table renames through its column constraint rather than through the column label,
 * because converting a plain table to a structured table reads the constraint; the surface supplies
 * that variant as `withColumnNameSet`.
 */
export function renameColumn(params: TableMutationParams, index: number, name: string): void {
  const {table, withColumnNameSet} = params.getTableProps();
  saveSelection(params);
  persistTable(params, {
    table: withColumnNameSet?.(table, index, name) ?? TableUtil.withColumnNameSet(table, index, name),
    tableOp: {type: 'noop'},
  });
}

/**
 * Set the constraint of the column at `index`, optionally merging `headerRowCell` into that
 * column's header cell, checkpointing the current selection for undo first.
 *
 * The change is persisted with an `eval-columns` op for the column: a constraint change may alter
 * how the column's cells evaluate, and assuming it does is the safe choice.
 */
export function setColumnConstraint(
  params: TableMutationParams,
  index: number,
  columnConstraint: ColumnConstraint,
  headerRowCell: Partial<Cell> = {}
): void {
  const {table} = params.getTableProps();
  saveSelection(params);
  const newTable = produce(table, (draftTable) => {
    draftTable.columnConstraints[index] = columnConstraint;
    draftTable.table[0][index] = {
      ...draftTable.table[0][index],
      ...headerRowCell,
    };
  });
  persistTable(params, {table: newTable, tableOp: {type: 'eval-columns', colIndexes: [index]}});
}
